import { Search } from "@vapor/react-icons";
import {
    Autocomplete,
    IconButton,
    InputAdornment,
    ListItem,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@vapor/react-material";
import { type ReactElement, type SyntheticEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { TaxPayerDetailsDto } from "../../../../../core/api/use-get-fulfilment-tax-payers/TaxPayerDetailsDto";

export interface FulfilmentTaxPayerListFilterProps {
    taxPayers: TaxPayerDetailsDto[];
    value: TaxPayerDetailsDto | null;
    onFilterChange: (taxPayer: TaxPayerDetailsDto | null) => void;
}

export const FulfilmentTaxPayerListFilter = ({
    taxPayers,
    value,
    onFilterChange,
}: FulfilmentTaxPayerListFilterProps): ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();

    const handleTaxPayerChange = useCallback(
        (
            _: SyntheticEvent<Element, Event>,
            newValue: TaxPayerDetailsDto | null,
        ) => {
            onFilterChange(newValue);
        },
        [onFilterChange],
    );

    return (
        <Stack alignItems="end" direction="row">
            <Autocomplete
                // Vapor's Autocomplete defaults disableClearable to true;
                // the filter must be clearable to get back the full list
                disableClearable={false}
                disabled={taxPayers.length === 0}
                // the search adornment replaces the dropdown arrow; keep only
                // the clear icon from the default end adornment
                forcePopupIcon={false}
                getOptionLabel={(option: TaxPayerDetailsDto) =>
                    option.businessName
                        ? option.businessName
                        : `${option.name} ${option.surname}`
                }
                isOptionEqualToValue={(option, value) => {
                    return option.workspaceId === value.workspaceId;
                }}
                filterOptions={(options, { inputValue }) => {
                    return options.filter((option) => {
                        const name = option.businessName
                            ? option.businessName
                            : `${option.name} ${option.surname}`;
                        return (
                            name
                                .toLowerCase()
                                .includes(inputValue.toLowerCase()) ||
                            option.taxIdentifier
                                .toLowerCase()
                                .includes(inputValue.toLowerCase()) ||
                            option.vatIdentifier
                                .toLowerCase()
                                .includes(inputValue.toLowerCase())
                        );
                    });
                }}
                noOptionsText={t(
                    "views.home.components.fulfilment_list_header.filters.taxPayer.no_options_text",
                )}
                onChange={handleTaxPayerChange}
                options={taxPayers}
                renderOption={(props, option) => {
                    return (
                        <ListItem {...props} key={option.workspaceId}>
                            <Stack>
                                {option.businessName && (
                                    <Typography>
                                        {option.businessName}
                                    </Typography>
                                )}
                                {!option.businessName && (
                                    <Typography>
                                        {option.name} {option.surname}
                                    </Typography>
                                )}
                                <Typography
                                    color={
                                        theme.palette.primary.textSubduedColor
                                    }
                                >
                                    {option.taxIdentifier}
                                </Typography>
                            </Stack>
                        </ListItem>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t(
                            "views.home.components.fulfilment_list_header.filters.taxPayer.label",
                        )}
                        placeholder={t(
                            "views.home.components.fulfilment_list_header.filters.taxPayer.placeholder",
                        )}
                        InputLabelProps={{
                            ...params.InputLabelProps,
                            shrink: true,
                        }}
                        InputProps={{
                            ...params.InputProps,
                            sx: { width: "20vw" },
                            endAdornment: (
                                <>
                                    {params.InputProps.endAdornment}
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            sx={{
                                                pointerEvents: "none",
                                                background:
                                                    "linear-gradient(-26.56505117707799deg, rgb(0, 195, 234) 0%, rgb(0, 144, 209) 100%)",
                                            }}
                                        >
                                            <Search color="white" />
                                        </IconButton>
                                    </InputAdornment>
                                </>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                padding: 0,
                                paddingLeft: "4px",
                            },
                            "& .MuiAutocomplete-inputRoot": {
                                paddingRight: "0 !important",
                            },
                            // keep the clear icon out from under the search
                            // button, which sits flush at the input's right edge
                            "& .MuiAutocomplete-endAdornment": {
                                position: "static",
                                transform: "none",
                            },
                        }}
                    />
                )}
                value={value}
            />
        </Stack>
    );
};
